// Pure helpers for the shared spatial model. Acts on plain JS objects so the
// 2D and 3D editors operate on identical data shapes.

export const EMPTY_GRAPH = Object.freeze({
  nodes: [],
  edges: [],
  anchors: [],
  destinations: [],
  zones: [],
  beacons: [],
})

export const NODE_TYPES = [
  'start', 'hallway', 'intersection', 'turn',
  'doorway', 'elevator', 'stair', 'ramp',
  'transition', 'checkpoint', 'destination', 'landmark',
]

export const ANCHOR_TYPES = [
  'qr', 'visual_checkpoint', 'entry', 'elevator', 'stair', 'destination',
]

export const EDGE_TYPES = [
  'hallway', 'doorway', 'ramp', 'elevator_transition', 'stair_transition',
  'restricted', 'temporary_detour',
]

// Server snapshots use position_x/position_y/position_z while the editor
// works in {x,y,z}. Project the server form onto editor form on clone so
// downstream code (auto-fit, drawing, hit-testing) sees consistent fields
// regardless of whether a node was loaded from the API or just authored.
function withXYZ(o) {
  return {
    ...o,
    x: o.x ?? o.position_x ?? 0,
    y: o.y ?? o.position_y ?? 0,
    z: o.z ?? o.position_z ?? 0,
  }
}

export function cloneGraph(graph) {
  return {
    nodes: (graph.nodes || []).map((n) => withXYZ(n)),
    edges: (graph.edges || []).map((e) => ({ ...e })),
    anchors: (graph.anchors || []).map((a) => withXYZ(a)),
    destinations: (graph.destinations || []).map((d) => ({ ...d })),
    zones: (graph.zones || []).map((z) => ({ ...z, polygon: [...(z.polygon || [])] })),
    beacons: (graph.beacons || []).map((b) => withXYZ(b)),
  }
}

let counter = 0
function localId(prefix) {
  counter += 1
  return `${prefix}_${Date.now().toString(36)}_${counter}`
}

function distance3(a, b) {
  const dx = (a.x ?? a.position_x ?? 0) - (b.x ?? b.position_x ?? 0)
  const dy = (a.y ?? a.position_y ?? 0) - (b.y ?? b.position_y ?? 0)
  const dz = (a.z ?? a.position_z ?? 0) - (b.z ?? b.position_z ?? 0)
  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}

export function findNode(graph, id) {
  return graph.nodes.find((n) => n.id === id) || null
}

export function findEdge(graph, id) {
  return graph.edges.find((e) => e.id === id) || null
}

export function findAnchor(graph, id) {
  return graph.anchors.find((a) => a.id === id) || null
}

export function findDestination(graph, id) {
  return graph.destinations.find((d) => d.id === id) || null
}

export function findZone(graph, id) {
  return graph.zones.find((z) => z.id === id) || null
}

export function findBeacon(graph, id) {
  return graph.beacons.find((b) => b.id === id) || null
}

export function findById(graph, kind, id) {
  switch (kind) {
    case 'node': return findNode(graph, id)
    case 'edge': return findEdge(graph, id)
    case 'anchor': return findAnchor(graph, id)
    case 'destination': return findDestination(graph, id)
    case 'zone': return findZone(graph, id)
    case 'beacon': return findBeacon(graph, id)
    default: return null
  }
}

// ---------- nodes ----------
export function addNode(graph, input) {
  const node = {
    id: input.id || localId('node'),
    floor_id: input.floor_id || null,
    node_type: input.node_type || 'hallway',
    name: input.name || '',
    x: input.x ?? input.position_x ?? 0,
    y: input.y ?? input.position_y ?? 0,
    z: input.z ?? input.position_z ?? 0,
    heading_hint: input.heading_hint ?? null,
    accessibility_flags: input.accessibility_flags || {},
    zone_id: input.zone_id || null,
    metadata: input.metadata || {},
  }
  return { graph: { ...graph, nodes: [...graph.nodes, node] }, created: node }
}

export function updateNode(graph, id, patch) {
  const nodes = graph.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n))
  let edges = graph.edges
  if ('x' in patch || 'y' in patch || 'z' in patch) {
    edges = graph.edges.map((edge) => {
      if (edge.from_node_id !== id && edge.to_node_id !== id) return edge
      return recomputeEdgeDistance(edge, nodes)
    })
  }
  return { graph: { ...graph, nodes, edges } }
}

export function removeNode(graph, id) {
  const nodes = graph.nodes.filter((n) => n.id !== id)
  const incident = graph.edges.filter((e) => e.from_node_id === id || e.to_node_id === id)
  const remainingEdges = graph.edges.filter((e) => e.from_node_id !== id && e.to_node_id !== id)
  const bridgedEdges = bridgeEdgesAcrossNode(id, incident, remainingEdges, nodes)
  const edges = [...remainingEdges, ...bridgedEdges]
  const anchors = graph.anchors.map((a) => (a.linked_node_id === id ? { ...a, linked_node_id: null } : a))
  const destinations = graph.destinations.map((d) => (d.linked_node_id === id ? { ...d, linked_node_id: null } : d))
  return { graph: { ...graph, nodes, edges, anchors, destinations } }
}

// When a middle node X is deleted, preserve connectivity by adding "bridge"
// edges that join X's neighbors directly. Honors directionality and
// accessibility: a one-way A→X plus a one-way X→B becomes A→B; two two-way
// edges become a single two-way bridge; a bridged edge is accessible only
// when both source edges were. Skips bridges that would duplicate an
// existing edge or create a self-loop.
function bridgeEdgesAcrossNode(removedId, incident, remainingEdges, nodes) {
  const inNeighbors = []
  const outNeighbors = []
  for (const e of incident) {
    const isFromRemoved = e.from_node_id === removedId
    const otherId = isFromRemoved ? e.to_node_id : e.from_node_id
    if (otherId === removedId) continue
    if (e.directionality === 'two_way') {
      inNeighbors.push({ otherId, edge: e })
      outNeighbors.push({ otherId, edge: e })
    } else if (isFromRemoved) {
      outNeighbors.push({ otherId, edge: e })
    } else {
      inNeighbors.push({ otherId, edge: e })
    }
  }

  const candidates = new Map()
  for (const inN of inNeighbors) {
    for (const outN of outNeighbors) {
      if (inN.otherId === outN.otherId) continue
      const key = `${inN.otherId}->${outN.otherId}`
      if (candidates.has(key)) continue
      candidates.set(key, {
        from: inN.otherId,
        to: outN.otherId,
        edge_type: inN.edge.edge_type || outN.edge.edge_type || 'hallway',
        accessible: inN.edge.accessible !== false && outN.edge.accessible !== false,
        allowed_modes: inN.edge.allowed_modes || outN.edge.allowed_modes || ['walk'],
        penalty_weight: Math.max(inN.edge.penalty_weight ?? 1, outN.edge.penalty_weight ?? 1),
        floor_id: inN.edge.floor_id || outN.edge.floor_id || null,
      })
    }
  }

  const merged = []
  const consumed = new Set()
  for (const [key, cand] of candidates) {
    if (consumed.has(key)) continue
    const reverseKey = `${cand.to}->${cand.from}`
    const reverse = candidates.get(reverseKey)
    consumed.add(key)
    if (reverse) consumed.add(reverseKey)
    merged.push({ ...cand, directionality: reverse ? 'two_way' : 'one_way' })
  }

  const result = []
  for (const b of merged) {
    const fromNode = nodes.find((n) => n.id === b.from)
    const toNode = nodes.find((n) => n.id === b.to)
    if (!fromNode || !toNode) continue
    const existingForward = remainingEdges.some(
      (e) => e.from_node_id === b.from && e.to_node_id === b.to,
    )
    const existingReverse = remainingEdges.some(
      (e) => e.from_node_id === b.to && e.to_node_id === b.from,
    )
    const existingTwoWay = remainingEdges.some(
      (e) =>
        e.directionality === 'two_way' &&
        ((e.from_node_id === b.from && e.to_node_id === b.to) ||
          (e.from_node_id === b.to && e.to_node_id === b.from)),
    )
    if (existingTwoWay) continue
    if (existingForward) continue
    if (b.directionality === 'two_way' && existingReverse) continue
    result.push({
      id: localId('edge'),
      floor_id: b.floor_id,
      from_node_id: b.from,
      to_node_id: b.to,
      edge_type: b.edge_type,
      distance: distance3(fromNode, toNode),
      directionality: b.directionality,
      accessible: b.accessible,
      allowed_modes: b.allowed_modes,
      penalty_weight: b.penalty_weight,
      transition_metadata: {},
      notes: 'auto-bridged on node delete',
    })
  }
  return result
}

// ---------- edges ----------
export function addEdge(graph, input) {
  if (!input.from_node_id || !input.to_node_id) {
    throw new Error('Edge requires from_node_id and to_node_id')
  }
  if (input.from_node_id === input.to_node_id) {
    throw new Error('Edge cannot connect a node to itself')
  }
  const exists = graph.edges.some(
    (e) => e.from_node_id === input.from_node_id && e.to_node_id === input.to_node_id,
  )
  if (exists) {
    throw new Error('Edge already exists between these nodes')
  }
  const from = findNode(graph, input.from_node_id)
  const to = findNode(graph, input.to_node_id)
  const edge = {
    id: input.id || localId('edge'),
    floor_id: input.floor_id || null,
    from_node_id: input.from_node_id,
    to_node_id: input.to_node_id,
    edge_type: input.edge_type || 'hallway',
    distance: input.distance ?? (from && to ? distance3(from, to) : 0),
    directionality: input.directionality || 'two_way',
    accessible: input.accessible !== false,
    allowed_modes: input.allowed_modes || ['walk'],
    penalty_weight: input.penalty_weight ?? 1,
    transition_metadata: input.transition_metadata || {},
    notes: input.notes || '',
  }
  return { graph: { ...graph, edges: [...graph.edges, edge] }, created: edge }
}

/** Connect a graph node to a destination: ensures the destination has a linked_node_id (creates a node at the destination pose if needed), then adds an edge from fromNodeId to that node. */
export function linkDestinationWithEdge(graph, fromNodeId, destId) {
  if (!fromNodeId || !destId) {
    throw new Error('linkDestinationWithEdge: missing node or destination id')
  }
  const dest = findDestination(graph, destId)
  if (!dest) throw new Error('Destination not found')
  if (!findNode(graph, fromNodeId)) throw new Error('Start node not found')

  let g = graph
  let targetNodeId = dest.linked_node_id

  if (!targetNodeId) {
    const nodeRes = addNode(g, {
      x: dest.x,
      y: dest.y ?? 0,
      z: dest.z,
      name: dest.name ? String(dest.name) : 'Destination',
      node_type: 'hallway',
    })
    g = nodeRes.graph
    targetNodeId = nodeRes.created.id
    g = updateDestination(g, destId, { linked_node_id: targetNodeId }).graph
  }

  if (targetNodeId === fromNodeId) {
    throw new Error('Destination already links to this node; no edge needed.')
  }

  return addEdge(g, { from_node_id: fromNodeId, to_node_id: targetNodeId })
}

export function updateEdge(graph, id, patch) {
  const edges = graph.edges.map((e) => (e.id === id ? { ...e, ...patch } : e))
  return { graph: { ...graph, edges } }
}

// Insert a new node at `point` along edge `id`, replacing the original edge
// with two segments that preserve direction, type, accessibility, modes and
// penalty. The split point is snapped onto the segment so it always lies
// exactly on the line.
export function splitEdgeAtPoint(graph, id, point, nodeInput) {
  const edge = findEdge(graph, id)
  if (!edge) throw new Error('Edge not found')
  const from = findNode(graph, edge.from_node_id)
  const to = findNode(graph, edge.to_node_id)
  if (!from || !to) throw new Error('Edge endpoints missing')

  const snapped = projectOntoSegment2D(point, from, to)
  const newNode = {
    id: nodeInput?.id || localId('node'),
    floor_id: edge.floor_id || null,
    node_type: nodeInput?.node_type || 'hallway',
    name: nodeInput?.name || `Node ${graph.nodes.length + 1}`,
    x: snapped.x,
    y: nodeInput?.y ?? from.y ?? 0,
    z: snapped.z,
    heading_hint: nodeInput?.heading_hint ?? null,
    accessibility_flags: nodeInput?.accessibility_flags || {},
    zone_id: nodeInput?.zone_id || null,
    metadata: nodeInput?.metadata || {},
  }
  const nodes = [...graph.nodes, newNode]

  const inheritEdge = (overrides) => ({
    id: localId('edge'),
    floor_id: edge.floor_id || null,
    edge_type: edge.edge_type || 'hallway',
    directionality: edge.directionality || 'two_way',
    accessible: edge.accessible !== false,
    allowed_modes: Array.isArray(edge.allowed_modes) ? [...edge.allowed_modes] : ['walk'],
    penalty_weight: edge.penalty_weight ?? 1,
    transition_metadata: { ...(edge.transition_metadata || {}), split_from_edge_id: edge.id },
    notes: edge.notes ? `${edge.notes} (split)` : 'auto-split on add-node',
    ...overrides,
  })

  const segA = inheritEdge({
    from_node_id: edge.from_node_id,
    to_node_id: newNode.id,
  })
  segA.distance = distance3(from, newNode)
  const segB = inheritEdge({
    from_node_id: newNode.id,
    to_node_id: edge.to_node_id,
  })
  segB.distance = distance3(newNode, to)

  const edges = graph.edges.filter((e) => e.id !== edge.id).concat([segA, segB])
  return { graph: { ...graph, nodes, edges }, created: newNode, edges: [segA, segB] }
}

export function projectOntoSegment2D(point, a, b) {
  const ax = a.x ?? 0, az = a.z ?? 0
  const bx = b.x ?? 0, bz = b.z ?? 0
  const px = point.x ?? 0, pz = point.z ?? 0
  const dx = bx - ax
  const dz = bz - az
  const lenSq = dx * dx + dz * dz
  if (lenSq < 1e-9) return { x: ax, z: az }
  let t = ((px - ax) * dx + (pz - az) * dz) / lenSq
  t = Math.max(0.05, Math.min(0.95, t))
  return { x: ax + dx * t, z: az + dz * t }
}

export function removeEdge(graph, id) {
  return { graph: { ...graph, edges: graph.edges.filter((e) => e.id !== id) } }
}

function recomputeEdgeDistance(edge, nodes) {
  const from = nodes.find((n) => n.id === edge.from_node_id)
  const to = nodes.find((n) => n.id === edge.to_node_id)
  if (!from || !to) return edge
  return { ...edge, distance: distance3(from, to) }
}

// ---------- anchors ----------
export function addAnchor(graph, input) {
  const anchor = {
    id: input.id || localId('anchor'),
    floor_id: input.floor_id || null,
    anchor_type: input.anchor_type || 'qr',
    name: input.name || '',
    external_code: input.external_code || '',
    x: input.x ?? input.position_x ?? 0,
    y: input.y ?? input.position_y ?? 0,
    z: input.z ?? input.position_z ?? 0,
    rotation_yaw: input.rotation_yaw ?? 0,
    rotation_pitch: input.rotation_pitch ?? 0,
    rotation_roll: input.rotation_roll ?? 0,
    forward_x: input.forward_x ?? 0,
    forward_y: input.forward_y ?? 1,
    forward_z: input.forward_z ?? 0,
    up_x: input.up_x ?? 0,
    up_y: input.up_y ?? 0,
    up_z: input.up_z ?? 1,
    linked_node_id: input.linked_node_id || null,
    linked_zone_id: input.linked_zone_id || null,
    wall_height: input.wall_height ?? 0,
    mount_type: input.mount_type || 'floor',
    confidence_radius: input.confidence_radius ?? 0.5,
    status: input.status || 'planned',
    notes: input.notes || '',
    placard_image_bucket: input.placard_image_bucket || null,
    placard_image_storage_key: input.placard_image_storage_key || null,
    placard_physical_width_m: input.placard_physical_width_m ?? null,
  }
  return { graph: { ...graph, anchors: [...graph.anchors, anchor] }, created: anchor }
}

export function updateAnchor(graph, id, patch) {
  const anchors = graph.anchors.map((a) => (a.id === id ? { ...a, ...patch } : a))
  return { graph: { ...graph, anchors } }
}

export function removeAnchor(graph, id) {
  const anchors = graph.anchors.filter((a) => a.id !== id)
  const destinations = graph.destinations.map((d) => (d.linked_anchor_id === id ? { ...d, linked_anchor_id: null } : d))
  return { graph: { ...graph, anchors, destinations } }
}

// ---------- destinations ----------
export function addDestination(graph, input) {
  const dest = {
    id: input.id || localId('dest'),
    floor_id: input.floor_id || null,
    name: input.name || 'New Destination',
    category: input.category || 'general',
    linked_node_id: input.linked_node_id || null,
    linked_anchor_id: input.linked_anchor_id || null,
    description: input.description || '',
    aliases: input.aliases || [],
    tags: input.tags || [],
    is_public: input.is_public !== false,
    search_weight: input.search_weight ?? 1,
    accessibility_notes: input.accessibility_notes || '',
    status: input.status || 'draft',
    x: input.x ?? input.position_x ?? 0,
    y: input.y ?? input.position_y ?? 0,
    z: input.z ?? input.position_z ?? 0,
  }
  return { graph: { ...graph, destinations: [...graph.destinations, dest] }, created: dest }
}

export function updateDestination(graph, id, patch) {
  const destinations = graph.destinations.map((d) => (d.id === id ? { ...d, ...patch } : d))
  return { graph: { ...graph, destinations } }
}

export function removeDestination(graph, id) {
  return { graph: { ...graph, destinations: graph.destinations.filter((d) => d.id !== id) } }
}

// ---------- zones ----------
export function addZone(graph, input) {
  const zone = {
    id: input.id || localId('zone'),
    floor_id: input.floor_id || null,
    name: input.name || 'New Zone',
    zone_type: input.zone_type || 'corridor',
    polygon: input.polygon || [],
    z_range_min: input.z_range_min ?? 0,
    z_range_max: input.z_range_max ?? 3,
    notes: input.notes || '',
    status: input.status || 'draft',
  }
  return { graph: { ...graph, zones: [...graph.zones, zone] }, created: zone }
}

export function updateZone(graph, id, patch) {
  const zones = graph.zones.map((z) => (z.id === id ? { ...z, ...patch } : z))
  return { graph: { ...graph, zones } }
}

export function removeZone(graph, id) {
  return { graph: { ...graph, zones: graph.zones.filter((z) => z.id !== id) } }
}

// ---------- beacons ----------
export function addBeacon(graph, input) {
  const beacon = {
    id: input.id || localId('beacon'),
    floor_id: input.floor_id || null,
    name: input.name || 'New Beacon',
    hardware_vendor: input.hardware_vendor || '',
    uuid: input.uuid || '',
    major: input.major ?? 0,
    minor: input.minor ?? 0,
    x: input.x ?? input.position_x ?? 0,
    y: input.y ?? input.position_y ?? 0,
    z: input.z ?? input.position_z ?? 2.4,
    rotation_yaw: input.rotation_yaw ?? 0,
    tx_power: input.tx_power ?? -59,
    advertising_interval: input.advertising_interval ?? 100,
    battery_type: input.battery_type || 'CR2477',
    install_date: input.install_date || null,
    maintenance_interval: input.maintenance_interval || null,
    linked_zone_id: input.linked_zone_id || null,
    linked_anchor_id: input.linked_anchor_id || null,
    status: input.status || 'planned',
    notes: input.notes || '',
  }
  return { graph: { ...graph, beacons: [...graph.beacons, beacon] }, created: beacon }
}

export function updateBeacon(graph, id, patch) {
  const beacons = graph.beacons.map((b) => (b.id === id ? { ...b, ...patch } : b))
  return { graph: { ...graph, beacons } }
}

export function removeBeacon(graph, id) {
  return { graph: { ...graph, beacons: graph.beacons.filter((b) => b.id !== id) } }
}
